/* eslint-disable @typescript-eslint/no-explicit-any */
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { TextField as MuiTextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Form, RenderFuncProps } from '../form-component/Form';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import { FavoritesToggle } from '../page-component/pageStyles';
import { useFilter } from '@dispatch/shared/hooks';
import { DynamicFilterModalProps } from './FilterTypes';
import { Filter } from '../page-component/PageTypes';
import {
  buildFilterLookup,
  cleanFilters,
  handleDateRange,
  normalizeFilterValueForPersistence,
} from './utils';
import { FilterList } from './FilterList';
import { SelectedChips } from './SelectedChips';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuidv4 } from 'uuid';
import {
  MultiSelectOption,
  ResourceAttribute,
  TaskFilterGroup,
} from '@dispatch/shared/types';
import { useNotification } from '@dispatch/web/hooks';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { debounce, isNil, isNull } from 'lodash';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  AddToFavoritesModal,
  defaultFilters,
  DeleteConfirmationModal,
  EmptyGroupDropZone,
  FilterItem,
  GroupItem,
  schema,
  SortableFilterItem,
  SortableGroupItem,
} from './DynamicFilterComponents';
import { useDroppable } from '@dnd-kit/core';
import { getRouteSegment } from './useFilterRouteSync';
import {
  buildContextKeyMap,
  flattenContextFilters,
  groupFiltersForContext,
} from './filterContextUtils';

export function DynamicFilterModal<
  PersistentFilters extends Record<string, any>
>({
  filters = [],
  storedFilters,
  handleSaveFilters,
  handleOrderIndexBulkUpdate,
  handleGroupsOrderIndexBulkUpdate,
  handleSaveFilterGroup,
  handleDeleteFilter,
  handleDeleteFilterGroup,
  setLossStateFilterInput,
  setRsrcStateFilterInput,
  allowSaveFilters = false,
  showFilterState,
  syncRoutes,
}: DynamicFilterModalProps<PersistentFilters>) {
  const { tenantId } = useCurrentUser();
  const filterContext = useFilter();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'normal' | 'edit'>('normal');
  const [showFiltersInternal, setShowFiltersInternal] = React.useState<
    Record<string, boolean>
  >({});
  const [showFavorites, setShowFavorites] = React.useState(false);
  const [addFavorite, setAddFavorite] = React.useState(false);
  const [showChips, setShowChips] = React.useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<GroupItem | null>(null);

  React.useEffect(() => {
    if (!isNil(showFilterState)) return;
    const showStates = filters.reduce((acc, filter) => {
      const key = filter.label.toLowerCase().replace(/ /g, '_');
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);

    setShowFiltersInternal(showStates);
  }, [filters, showFilterState?.showFilterState]);

  const [pendingGroupUpdates, setPendingGroupUpdates] = useState<
    Record<string, GroupItem>
  >({});
  const [pendingFilterUpdates, setPendingFilterUpdates] = useState<
    Record<string, FilterItem>
  >({});
  const [filtersInGroupToDelete, setFiltersInGroupToDelete] = useState<
    FilterItem[]
  >([]);
  const [selectedGroupForNewFilter, setSelectedGroupForNewFilter] = useState<
    string | null
  >(null);
  const [groupOrderIndices, setGroupOrderIndices] = useState<
    Record<string, number>
  >({});
  const [createdGroups, setCreatedGroups] = useState<GroupItem[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [addFavoriteModalOpen, setAddFavoriteModalOpen] = useState(false);

  const [filterAssignments, setFilterAssignments] = useState<
    Record<string, string | null>
  >({});

  const [filterOrderIndices, setFilterOrderIndices] = useState<
    Record<string, number>
  >({});

  const customCollisionDetection = useCallback((args: any) => {
    return closestCenter(args);
  }, []);

  const prevOpen = React.useRef(open);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { notifyError } = useNotification();

  const fuzzySearch = (query: string, text: string): boolean => {
    if (!query) return true;

    const searchQuery = query.toLowerCase();
    const searchText = text.toLowerCase();

    if (searchText.includes(searchQuery)) return true;

    let queryIndex = 0;
    for (
      let i = 0;
      i < searchText.length && queryIndex < searchQuery.length;
      i++
    ) {
      if (searchText[i] === searchQuery[queryIndex]) {
        queryIndex++;
      }
    }

    return queryIndex === searchQuery.length;
  };

  const {
    setValue,
    control: formControl,
    formState: { errors: formErrors, isDirty, isValid },
    handleSubmit,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    context: filterContext,
    defaultValues: {
      favName: '',
      ...defaultFilters(filters),
    },
  });

  const currentFormValues = watch();

  const filterLookUp = React.useMemo(() => {
    return buildFilterLookup(filters);
  }, [filters]);

  const contextKeyMap = React.useMemo(
    () => buildContextKeyMap(filters),
    [filters]
  );
  const hasContextKeys = React.useMemo(
    () => filters.some((g) => g.contextKey),
    [filters]
  );

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  const filteredStoredFilters = useMemo(() => {
    if (!storedFilters || !searchQuery) return storedFilters;

    const groups = storedFilters.filter(
      (item: any) => item.groupId && !item.filterId && item.name
    );

    const filters = storedFilters.filter((item: any) => item.filterId);

    const matchingFilters = filters.filter((filter: any) =>
      fuzzySearch(searchQuery, filter?.filterName || '')
    );

    const matchingGroups = groups.filter((group: any) => {
      const groupMatches = fuzzySearch(searchQuery, group.name);
      const hasMatchingFilters = matchingFilters.some(
        (filter: any) => filter.groupId === group.groupId
      );
      return groupMatches || hasMatchingFilters;
    });

    return [...matchingGroups, ...matchingFilters];
  }, [storedFilters, searchQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const dragDropData = useMemo(() => {
    if (!filteredStoredFilters || filteredStoredFilters.length === 0) {
      return {
        groups: createdGroups.sort((a, b) => {
          const aOrder =
            groupOrderIndices[a.id] !== undefined
              ? groupOrderIndices[a.id]
              : a.orderIndex;
          const bOrder =
            groupOrderIndices[b.id] !== undefined
              ? groupOrderIndices[b.id]
              : b.orderIndex;
          return aOrder - bOrder;
        }),
        allFilters: [],
        groupedFilters: {},
        ungroupedFilters: [],
      };
    }

    const dbGroups: GroupItem[] = [];
    const dbFilters: any[] = [];

    filteredStoredFilters.forEach((item: any) => {
      if (item.groupId && !item.filterId && item.name) {
        dbGroups.push({
          id: item.groupId,
          name: item.name,
          type: 'group' as const,
          orderIndex: item.orderIndex || 0,
        });
      } else if (item.filterId) {
        dbFilters.push(item);
      }
    });

    const allGroups = [...createdGroups, ...dbGroups];

    const uniqueGroups = allGroups.reduce((acc, group) => {
      const existing = acc.find((g) => g.id === group.id);
      if (!existing) {
        acc.push(group);
      } else if (dbGroups.find((g) => g.id === group.id)) {
        const index = acc.findIndex((g) => g.id === group.id);
        acc[index] = group;
      }
      return acc;
    }, [] as GroupItem[]);

    setCollapsedGroups((prev) => {
      const newCollapsed = new Set(prev);
      uniqueGroups.forEach((group) => {
        if (!prev.has(group.id)) {
          newCollapsed.add(group.id);
        }
      });
      return newCollapsed;
    });

    const groups: GroupItem[] = uniqueGroups.sort((a, b) => {
      const aOrder =
        groupOrderIndices[a.id] !== undefined
          ? groupOrderIndices[a.id]
          : a.orderIndex;
      const bOrder =
        groupOrderIndices[b.id] !== undefined
          ? groupOrderIndices[b.id]
          : b.orderIndex;
      return aOrder - bOrder;
    });

    const transformedFilters: FilterItem[] = dbFilters.map(
      (filter: any, index: number) => {
        const filterId = filter.filterId || filter.id || `filter-${index}`;

        let groupId: string | null = null;

        if (filterAssignments[filterId] !== undefined) {
          groupId = filterAssignments[filterId];
        } else if (filter.groupId) {
          groupId = filter.groupId;
        }

        let orderIndex;
        if (filterOrderIndices[filterId] !== undefined) {
          orderIndex = filterOrderIndices[filterId];
        } else if (filter.orderIndex !== undefined) {
          orderIndex = filter.orderIndex;
        } else {
          orderIndex = 0;
        }

        return {
          ...filter,
          id: filterId,
          filterId: filterId,
          filterName: filter.filterName || filter.name || 'Unnamed Filter',
          groupId: groupId,
          orderIndex: orderIndex,
          type: 'filter' as const,
        };
      }
    );

    const groupedFilters = groups.reduce((acc, group) => {
      acc[group.id] = transformedFilters
        .filter((filter) => filter.groupId === group.id)
        .sort((a, b) => a.orderIndex - b.orderIndex);
      return acc;
    }, {} as Record<string, FilterItem[]>);

    const ungroupedFilters = transformedFilters
      .filter((filter) => !filter.groupId)
      .sort((a, b) => a.orderIndex - b.orderIndex);

    return {
      groups,
      allFilters: transformedFilters,
      groupedFilters,
      ungroupedFilters,
    };
  }, [
    filteredStoredFilters,
    filterAssignments,
    filterOrderIndices,
    createdGroups,
    groupOrderIndices,
  ]);

  const debouncedBatchUpdateFilters = useCallback(
    debounce(async (filtersToUpdate: Record<string, FilterItem>) => {
      if (Object.keys(filtersToUpdate).length === 0) return;

      const filterUpdates = Object.values(filtersToUpdate);

      try {
        await handleOrderIndexBulkUpdate?.(filterUpdates as any);

        setPendingFilterUpdates({});
      } catch (error) {
        notifyError('Update Failed', 'Failed to save filter order changes');
      }
    }, 500),
    [handleOrderIndexBulkUpdate, notifyError]
  );

  const debouncedBatchUpdateGroups = useCallback(
    debounce(async (groupsToUpdate: Record<string, GroupItem>) => {
      if (Object.keys(groupsToUpdate).length === 0) return;

      const groupUpdates = Object.values(groupsToUpdate);

      try {
        const completeGroupList = groupUpdates
          .map((group) => ({
            ...group,
            orderIndex: group.orderIndex,
            groupId: group.id,
            tenantId,
            type: undefined,
          }))
          .map(({ type, id, ...groupWithoutType }) => groupWithoutType)
          .sort((a, b) => a.orderIndex - b.orderIndex);

        await handleGroupsOrderIndexBulkUpdate?.(completeGroupList);

        setPendingGroupUpdates({});
      } catch (error) {
        notifyError('Update Failed', 'Failed to save group order changes');
      }
    }, 500),
    [handleGroupsOrderIndexBulkUpdate, notifyError, tenantId]
  );

  const queueFilterUpdate = useCallback((filter: FilterItem) => {
    setPendingFilterUpdates((prev) => ({
      ...prev,
      [filter.filterId]: filter,
    }));
  }, []);

  const queueGroupUpdate = useCallback((group: GroupItem) => {
    setPendingGroupUpdates((prev) => ({
      ...prev,
      [group.id]: group,
    }));
  }, []);

  React.useEffect(() => {
    if (Object.keys(pendingGroupUpdates).length > 0) {
      debouncedBatchUpdateGroups(pendingGroupUpdates);
    }
  }, [pendingGroupUpdates, debouncedBatchUpdateGroups]);

  React.useEffect(() => {
    if (Object.keys(pendingFilterUpdates).length > 0) {
      debouncedBatchUpdateFilters(pendingFilterUpdates);
    }
  }, [pendingFilterUpdates, debouncedBatchUpdateFilters]);

  const handleConfirmDeleteGroup = useCallback(async () => {
    if (groupToDelete) {
      try {
        await handleDeleteFilterGroup?.(groupToDelete.id);

        const filtersInGroup = dragDropData.allFilters.filter(
          (filter) => filter.groupId === groupToDelete.id
        );

        if (filtersInGroup.length > 0) {
          const updatedAssignments = { ...filterAssignments };
          const updatedOrderIndices = { ...filterOrderIndices };

          const maxUngroupedOrder = Math.max(
            ...dragDropData.ungroupedFilters.map((f) =>
              updatedOrderIndices[f.id] !== undefined
                ? updatedOrderIndices[f.id]
                : f.orderIndex
            ),
            -1
          );

          filtersInGroup.forEach((filter, index) => {
            updatedAssignments[filter.id] = null;
            updatedOrderIndices[filter.id] = maxUngroupedOrder + 1 + index;
          });

          setFilterAssignments(updatedAssignments);
          setFilterOrderIndices(updatedOrderIndices);

          const updatedFilters = filtersInGroup.map((filter) => ({
            ...filter,
            groupId: null,
            orderIndex: updatedOrderIndices[filter.id],
          }));

          updatedFilters.forEach((filter) => {
            queueFilterUpdate(filter);
          });
        }

        setCreatedGroups((prevGroups) =>
          prevGroups.filter((g) => g.id !== groupToDelete.id)
        );

        setGroupOrderIndices((prev) => {
          const updated = { ...prev };
          delete updated[groupToDelete.id];
          return updated;
        });

        setFilterOrderIndices((prev) => ({ ...prev }));

        setDeleteConfirmModalOpen(false);
        setGroupToDelete(null);
        setFiltersInGroupToDelete([]);
      } catch (error) {
        notifyError('Group Deletion Failed', 'Could not delete group');
      }
    }
  }, [
    groupToDelete,
    handleDeleteFilterGroup,
    dragDropData.allFilters,
    dragDropData.ungroupedFilters,
    filterAssignments,
    filterOrderIndices,
    notifyError,
    queueFilterUpdate,
  ]);

  const handleCancelDeleteGroup = useCallback(() => {
    setDeleteConfirmModalOpen(false);
    setGroupToDelete(null);
    setFiltersInGroupToDelete([]);
  }, []);

  const handleEditGroup = useCallback((group: GroupItem) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
  }, []);

  const handleSaveGroupEdit = useCallback(async () => {
    if (!editingGroupId || !editGroupName.trim()) {
      notifyError('Invalid Group Name', 'Group name cannot be empty');
      return;
    }

    const trimmedName = editGroupName.trim();

    const existingGroup = dragDropData.groups.find(
      (g) =>
        g.id !== editingGroupId &&
        g.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingGroup) {
      notifyError(
        'Duplicate Group Name',
        `A group named "${trimmedName}" already exists. Please choose a different name.`
      );
      return;
    }

    const group = dragDropData.groups.find((g) => g.id === editingGroupId);
    if (!group) {
      notifyError('Error', 'Group not found');
      return;
    }

    try {
      const updatedGroupData: TaskFilterGroup = {
        name: trimmedName,
        orderIndex: group.orderIndex,
        groupId: group.id,
        tenantId,
      };

      await handleSaveFilterGroup?.(updatedGroupData);

      setCreatedGroups((prevGroups) =>
        prevGroups.map((g) =>
          g.id === editingGroupId ? { ...g, name: trimmedName } : g
        )
      );

      setEditingGroupId(null);
      setEditGroupName('');
    } catch (error) {
      notifyError('Group Update Failed', 'Could not update group name');
    }
  }, [
    editingGroupId,
    editGroupName,
    dragDropData.groups,
    handleSaveFilterGroup,
    tenantId,
    notifyError,
  ]);

  const handleCancelGroupEdit = useCallback(() => {
    setEditingGroupId(null);
    setEditGroupName('');
  }, []);

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      const group = dragDropData.groups.find((g) => g.id === groupId);

      if (!group) {
        notifyError('Error', 'Group not found');
        return;
      }

      const filtersInGroup = dragDropData.allFilters.filter(
        (filter) => filter.groupId === groupId
      );

      setGroupToDelete(group);
      setFiltersInGroupToDelete(filtersInGroup);
      setDeleteConfirmModalOpen(true);
    },
    [dragDropData.groups, dragDropData.allFilters, notifyError]
  );

  const initializeOrderIndices = useCallback(() => {
    if (dragDropData.allFilters.length > 0) {
      const newIndices: Record<string, number> = {};

      dragDropData.allFilters.forEach((filter) => {
        if (filterOrderIndices[filter.id] === undefined) {
          newIndices[filter.id] = filter.orderIndex;
        }
      });

      if (Object.keys(newIndices).length > 0) {
        setFilterOrderIndices((prev) => ({ ...prev, ...newIndices }));
      }
    }
  }, [dragDropData.allFilters, filterOrderIndices]);

  React.useEffect(() => {
    initializeOrderIndices();
  }, [initializeOrderIndices]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;
    const activeId = active.id;
    const overId = over.id;

    if (activeData?.type === 'group') {
      if (overData?.type === 'group') {
        const sortedGroups = dragDropData.groups.sort((a, b) => {
          const aOrder =
            groupOrderIndices[a.id] !== undefined
              ? groupOrderIndices[a.id]
              : a.orderIndex;
          const bOrder =
            groupOrderIndices[b.id] !== undefined
              ? groupOrderIndices[b.id]
              : b.orderIndex;
          return aOrder - bOrder;
        });

        const activeIndex = sortedGroups.findIndex((g) => g.id === activeId);
        const overIndex = sortedGroups.findIndex((g) => g.id === overId);

        if (
          activeIndex !== -1 &&
          overIndex !== -1 &&
          activeIndex !== overIndex
        ) {
          const reorderedGroups = [...sortedGroups];
          const [movedGroup] = reorderedGroups.splice(activeIndex, 1);
          reorderedGroups.splice(overIndex, 0, movedGroup);

          const updatedGroupOrderIndices = { ...groupOrderIndices };
          reorderedGroups.forEach((group, index) => {
            updatedGroupOrderIndices[group.id] = index;
          });

          setGroupOrderIndices(updatedGroupOrderIndices);

          reorderedGroups.forEach((group, index) => {
            const updatedGroup: GroupItem = {
              ...group,
              orderIndex: index,
            };

            queueGroupUpdate(updatedGroup);
          });
        }
      }
      return;
    }

    if (activeData?.type === 'filter') {
      let newGroupId: string | null = null;
      let shouldUpdateOrder = false;
      const updatedOrderIndices = { ...filterOrderIndices };
      const updatedAssignments = { ...filterAssignments };

      if (overData?.type === 'group') {
        newGroupId = overData.group.id;
      } else if (overData?.type === 'group-dropzone') {
        newGroupId = overData.groupId;
      } else if (overData?.type === 'filter') {
        newGroupId = overData.filter.groupId || null;
        shouldUpdateOrder = true;
      } else if (overId === 'ungrouped-dropzone') {
        newGroupId = null;
      } else {
        const droppedOnGroup = dragDropData.groups.find((g) => g.id === overId);
        if (droppedOnGroup) {
          newGroupId = droppedOnGroup.id;
        } else {
          const droppedOnUngroupedFilter = dragDropData.ungroupedFilters.find(
            (f) => f.id === overId
          );
          if (droppedOnUngroupedFilter) {
            newGroupId = null;
            shouldUpdateOrder = true;
          } else {
            newGroupId = null;
          }
        }
      }

      const activeFilter = dragDropData.allFilters.find(
        (f) => f.id === activeId
      );
      if (!activeFilter) return;

      const filterId = activeFilter.id;
      const oldGroupId = activeFilter.groupId;

      updatedAssignments[filterId] = newGroupId;

      if (
        shouldUpdateOrder &&
        (overData?.type === 'filter' ||
          dragDropData.ungroupedFilters.find((f) => f.id === overId))
      ) {
        const targetFilters = dragDropData.allFilters
          .filter((f) => f.groupId === newGroupId)
          .sort((a, b) => {
            const aOrder =
              updatedOrderIndices[a.id] !== undefined
                ? updatedOrderIndices[a.id]
                : a.orderIndex;
            const bOrder =
              updatedOrderIndices[b.id] !== undefined
                ? updatedOrderIndices[b.id]
                : b.orderIndex;
            return aOrder - bOrder;
          });

        const activeIndex = targetFilters.findIndex((f) => f.id === activeId);
        const overIndex = targetFilters.findIndex((f) => f.id === overId);

        if (
          activeIndex !== -1 &&
          overIndex !== -1 &&
          activeIndex !== overIndex
        ) {
          const reorderedFilters = [...targetFilters];
          const [movedFilter] = reorderedFilters.splice(activeIndex, 1);
          reorderedFilters.splice(overIndex, 0, movedFilter);

          reorderedFilters.forEach((filter, index) => {
            updatedOrderIndices[filter.id] = index;
          });
        }
      } else {
        if (newGroupId !== oldGroupId) {
          if (oldGroupId) {
            const oldGroupFilters = dragDropData.allFilters
              .filter((f) => f.groupId === oldGroupId && f.id !== filterId)
              .sort((a, b) => {
                const aOrder =
                  updatedOrderIndices[a.id] !== undefined
                    ? updatedOrderIndices[a.id]
                    : a.orderIndex;
                const bOrder =
                  updatedOrderIndices[b.id] !== undefined
                    ? updatedOrderIndices[b.id]
                    : b.orderIndex;
                return aOrder - bOrder;
              });

            oldGroupFilters.forEach((filter, index) => {
              updatedOrderIndices[filter.id] = index;
            });
          } else {
            const ungroupedFilters = dragDropData.allFilters
              .filter((f) => !f.groupId && f.id !== filterId)
              .sort((a, b) => {
                const aOrder =
                  updatedOrderIndices[a.id] !== undefined
                    ? updatedOrderIndices[a.id]
                    : a.orderIndex;
                const bOrder =
                  updatedOrderIndices[b.id] !== undefined
                    ? updatedOrderIndices[b.id]
                    : b.orderIndex;
                return aOrder - bOrder;
              });

            ungroupedFilters.forEach((filter, index) => {
              updatedOrderIndices[filter.id] = index;
            });
          }

          if (newGroupId) {
            const newGroupFilters = dragDropData.allFilters
              .filter((f) => f.groupId === newGroupId)
              .sort((a, b) => {
                const aOrder =
                  updatedOrderIndices[a.id] !== undefined
                    ? updatedOrderIndices[a.id]
                    : a.orderIndex;
                const bOrder =
                  updatedOrderIndices[b.id] !== undefined
                    ? updatedOrderIndices[b.id]
                    : b.orderIndex;
                return aOrder - bOrder;
              });

            updatedOrderIndices[filterId] = 0;

            newGroupFilters.forEach((filter, index) => {
              updatedOrderIndices[filter.id] = index + 1;
            });
          } else {
            const existingUngroupedFilters = dragDropData.allFilters
              .filter((f) => !f.groupId && f.id !== filterId)
              .sort((a, b) => {
                const aOrder =
                  updatedOrderIndices[a.id] !== undefined
                    ? updatedOrderIndices[a.id]
                    : a.orderIndex;
                const bOrder =
                  updatedOrderIndices[b.id] !== undefined
                    ? updatedOrderIndices[b.id]
                    : b.orderIndex;
                return aOrder - bOrder;
              });

            updatedOrderIndices[filterId] = 0;

            existingUngroupedFilters.forEach((filter, index) => {
              updatedOrderIndices[filter.id] = index + 1;
            });
          }
        }
      }

      setFilterOrderIndices(updatedOrderIndices);
      setFilterAssignments(updatedAssignments);

      const finalOrderedFilters = dragDropData.allFilters.map((filter) => ({
        ...filter,
        orderIndex: updatedOrderIndices[filter.id] ?? filter.orderIndex,
        groupId:
          updatedAssignments[filter.id] !== undefined
            ? updatedAssignments[filter.id]
            : filter.groupId,
      }));

      finalOrderedFilters.forEach((filter) => {
        const originalFilter = dragDropData.allFilters.find(
          (f) => f.id === filter.id
        );

        const orderChanged =
          originalFilter &&
          (originalFilter.orderIndex !== filter.orderIndex ||
            originalFilter.groupId !== filter.groupId);

        if (orderChanged) {
          queueFilterUpdate(filter);
        }
      });
    }
  };

  React.useEffect(() => {
    return () => {
      debouncedBatchUpdateFilters.cancel();
      debouncedBatchUpdateGroups.cancel();
    };
  }, [debouncedBatchUpdateFilters, debouncedBatchUpdateGroups]);

  const createNewGroup = useCallback(
    async (groupName: string): Promise<GroupItem | null> => {
      if (!groupName.trim()) {
        notifyError('Invalid Group Name', 'Group name cannot be empty');
        return null;
      }

      const trimmedName = groupName.trim();

      const existingGroup = dragDropData.groups.find(
        (group) => group.name.toLowerCase() === trimmedName.toLowerCase()
      );

      if (existingGroup) {
        notifyError(
          'Duplicate Group Name',
          `A group named "${trimmedName}" already exists. Please choose a different name.`
        );
        return null;
      }

      try {
        const newGroupData: TaskFilterGroup = {
          name: trimmedName,
          orderIndex: 0,
          groupId: uuidv4(),
          tenantId,
        };

        await handleSaveFilterGroup?.(newGroupData);

        const newGroup: GroupItem = {
          id: newGroupData.groupId,
          name: trimmedName,
          type: 'group' as const,
          orderIndex: 0,
        };

        const updatedGroupOrderIndices = { ...groupOrderIndices };

        dragDropData.groups.forEach((group) => {
          const currentOrder =
            updatedGroupOrderIndices[group.id] !== undefined
              ? updatedGroupOrderIndices[group.id]
              : group.orderIndex;
          updatedGroupOrderIndices[group.id] = currentOrder + 1;
        });

        updatedGroupOrderIndices[newGroup.id] = 0;

        setGroupOrderIndices(updatedGroupOrderIndices);
        setCreatedGroups((prevGroups) => [newGroup, ...prevGroups]);

        return newGroup;
      } catch (error) {
        notifyError(
          'Group Creation Failed',
          'Could not create new group. Please try again.'
        );
        return null;
      }
    },
    [
      dragDropData.groups,
      groupOrderIndices,
      handleSaveFilterGroup,
      notifyError,
      tenantId,
    ]
  );

  const handleApplyFilter = (filter: FilterItem) => {
    setShowChips(true);
    handleSetFormState(filter as any, filter.filterName);
  };

  const handleEditFilterAction = (filter: FilterItem) => {
    setMode('edit');
    setAddFavorite(true);
    setAddFavoriteModalOpen(true);

    const filterId = filter.filterId || filter.id;
    const currentGroupId =
      filterAssignments[filterId] || filter.groupId || null;
    setSelectedGroupForNewFilter(currentGroupId);

    const values = Object.entries(filter);
    setFilters(values, undefined, false);

    setValue('filterId', filterId);
  };

  const handleDeleteFilterAction = (filterId: string) => {
    handleDeleteFilter?.(filterId);
  };

  const handleToggle = () => {
    setOpen((event) => !event);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (addFavoriteModalOpen || deleteConfirmModalOpen) {
      return;
    }

    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleSetValue = (key: string, value: unknown) => {
    setValue(key, value);
  };

  const getClearEmptyFilters = React.useCallback(
    (objectFromArray: [string, any][], values: PersistentFilters) => {
      return objectFromArray.map((filter) => {
        if (filter[1] !== '') {
          if (Array.isArray(filter[1]) && filter[1]?.length > 0) {
            const field = filterLookUp[filter[0]];

            if (field?.[0]?.type === 'date-range') {
              handleDateRange(values, filter, field, handleSetValue);
            }

            if (field?.[0]?.type === 'async-select') {
              // No extraction - chips need full objects for display
            }
          }
          return filter;
        }

        return [];
      });
    },
    [filterLookUp, handleSetValue]
  );

  const onSubmit = (values: PersistentFilters) => {
    const objectFromArray = Object.entries(values);
    const clearEmptyFilters = getClearEmptyFilters(objectFromArray, values);
    const cleanedFilters = clearEmptyFilters.filter(
      (item) => item[0] !== undefined
    );

    const filterEntries = Object.fromEntries(cleanedFilters);
    if (
      filterEntries.employmentType === 'staff' &&
      filterEntries.contingentCompanies
    ) {
      filterEntries.contingentCompanies = [];
    }
    filterContext?.setFilters(
      hasContextKeys
        ? groupFiltersForContext(filterEntries, contextKeyMap)
        : { ...filterEntries }
    );
    reset({}, { keepValues: true });
    setOpen(false);
  };

  // On unmount, clear filters unless navigating to a route in the syncRoutes group.
  // When staying in sync group, only preserve taskFilters — resource filters are
  // view-specific and should not leak across views.
  // Uses functional updater (prev => ...) to read current state at cleanup time,
  // avoiding stale closure from the [] dependency array.
  React.useEffect(() => {
    return () => {
      if (syncRoutes) {
        const destination = getRouteSegment(window.location.pathname);
        if (syncRoutes.includes(destination)) {
          filterContext.setFilters((prev: Record<string, unknown>) => {
            const current = prev || {};
            if (current.taskFilters) {
              return { taskFilters: current.taskFilters };
            }
            return {};
          });
          return;
        }
      }
      filterContext.setFilters(() => ({}));
      filterContext.setDynamicFiltersDisplayed(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const toggleGroupCollapse = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  const setFilters = (
    values: [string, unknown][],
    filterName?: string,
    shouldDirty = true
  ) => {
    values.forEach(([key, value]) => {
      if (key === 'resourceAttributes') {
        return;
      }

      if (typeof value === 'string' && value.includes('[')) {
        setValue(key, JSON.parse(value), { shouldDirty });
      } else {
        setValue(key, value, { shouldDirty });
      }

      if (
        key === 'lossCounties' &&
        typeof value === 'string' &&
        !value.includes('[')
      ) {
        setValue(key, [{ title: value, value: 'error' } as MultiSelectOption]);
        notifyError(
          'Invalid Filter',
          `The filter "${filterName}" has an invalid "Loss County" selected.  Please remove all selected loss counties, reselect them, and save the changes to update the filter.`,
          {
            autoHideDuration: 10000,
          }
        );
      }
    });
  };

  const handleSetResourceAttributeFilters = (values: [string, string][]) => {
    const resourceAttributes = values.find(
      ([key, value]) => key === 'resourceAttributes' && !isNull(value)
    );

    if (resourceAttributes) {
      const resourceAttributesValue = JSON.parse(
        resourceAttributes[1]
      ) as ResourceAttribute[];

      resourceAttributesValue?.forEach((attribute) => {
        values.push([
          attribute.attributeDefinition,
          JSON.stringify(attribute.values),
        ]);
      });
    }
  };

  const handleSetFormState = (
    formFilters: PersistentFilters & { filterName?: string; filterId?: string },
    label: string
  ) => {
    const values = Object.entries(formFilters);
    handleSetResourceAttributeFilters(values);
    setFilters(values, formFilters?.filterName);

    if (mode === 'normal') {
      filterContext.setDynamicFiltersDisplayed({ label });
      setOpen(false);

      const cleanedFilters = cleanFilters(values, filterLookUp);
      const backToObject = Object.fromEntries(cleanedFilters);

      filterContext?.setFilters(
        hasContextKeys
          ? groupFiltersForContext(backToObject, contextKeyMap)
          : { ...backToObject }
      );
    }
  };

  const handleEditFilter = async (values: { filterName: string }) => {
    if (!editingGroupId && !currentFormValues.filterId) {
      notifyError('Error', 'No filter selected for editing');
      return;
    }

    const clearEmptyFilters = Object.entries(values).map((entry) =>
      normalizeFilterValueForPersistence(entry, filterLookUp)
    );

    const cleanedFilters = clearEmptyFilters.filter(
      (item) => item[0] !== undefined
    );

    const editFilterData = {
      ...Object.fromEntries(cleanedFilters),
      filterName: values.filterName,
      groupId: selectedGroupForNewFilter,
      filterId: currentFormValues.filterId,
    };

    try {
      await handleSaveFilters?.(editFilterData);

      if (currentFormValues.filterId) {
        setFilterAssignments((prev) => ({
          ...prev,
          [currentFormValues.filterId]: selectedGroupForNewFilter,
        }));
      }

      setAddFavorite(false);
      setAddFavoriteModalOpen(false);
      setSelectedGroupForNewFilter(null);
      reset();
      setMode('normal');
    } catch (error) {
      notifyError('Update Failed', 'Failed to update filter');
    }
  };

  const handleAddToFavorites = async (values: { filterName: string }) => {
    const clearEmptyFilters = Object.entries(values).map((entry) =>
      normalizeFilterValueForPersistence(entry, filterLookUp)
    );

    const cleanedFilters = clearEmptyFilters.filter(
      (item) => item[0] !== undefined
    );

    const newFilterId = `filter-${Date.now()}`;
    const updatedOrderIndices = { ...filterOrderIndices };
    const updatedAssignments = { ...filterAssignments };

    const filtersToUpdate: any[] = [];
    let targetFilters: FilterItem[] = [];

    if (selectedGroupForNewFilter) {
      updatedAssignments[newFilterId] = selectedGroupForNewFilter;

      targetFilters = dragDropData.allFilters
        .filter((filter) => filter.groupId === selectedGroupForNewFilter)
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    } else {
      updatedAssignments[newFilterId] = null;

      targetFilters = dragDropData.ungroupedFilters.sort(
        (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
      );
    }

    const newFilterData = {
      ...Object.fromEntries(cleanedFilters),
      filterId: newFilterId,
      filterName: values.filterName,
      groupId: selectedGroupForNewFilter,
      orderIndex: 0,
      tenantId,
      userId: '',
      isNew: true,
    };
    filtersToUpdate.push(newFilterData);
    updatedOrderIndices[newFilterId] = 0;

    targetFilters.forEach((filter, index) => {
      const newOrderIndex = index + 1;
      updatedOrderIndices[filter.id] = newOrderIndex;
      filtersToUpdate.push({
        ...filter,
        orderIndex: newOrderIndex,
        type: undefined,
      });
    });

    try {
      await handleOrderIndexBulkUpdate?.(filtersToUpdate as any);

      setFilterOrderIndices(updatedOrderIndices);
      setFilterAssignments(updatedAssignments);

      setAddFavorite(false);
      setAddFavoriteModalOpen(false);
      setSelectedGroupForNewFilter(null);

      handleSetFormState(
        newFilterData as PersistentFilters & {
          filterName?: string;
        },
        values.filterName
      );

      if (mode === 'normal') {
        reset({ ...values, filterName: '' });
      } else {
        reset();
        setMode('normal');
      }
    } catch (error) {
      notifyError('Save Failed', 'Failed to save filter and update order');
    }
  };

  const UngroupedDropZone = ({
    isOver,
    isFilterBeingDragged,
  }: {
    isOver: boolean;
    isFilterBeingDragged: boolean;
  }) => {
    const { setNodeRef } = useDroppable({
      id: 'ungrouped-dropzone',
    });

    return (
      <div
        ref={setNodeRef}
        style={{
          marginTop: '16px',
          padding: '16px',
          border: isOver ? '2px dashed #1976d2' : '2px dashed #ccc',
          borderRadius: '4px',
          backgroundColor: isOver ? '#e3f2fd' : '#f5f5f5',
          transition: 'all 0.2s ease',
          textAlign: 'center',
          color: isOver ? '#1976d2' : '#666',
          fontSize: '14px',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isFilterBeingDragged && isOver
          ? 'Drop here to ungroup filter'
          : 'Drop filters here to ungroup them'}
      </div>
    );
  };

  const favorites = () => {
    if (!dragDropData.groups.length && !dragDropData.ungroupedFilters.length) {
      return <p style={{ textAlign: 'center' }}>No saved filters</p>;
    }

    const { groups, groupedFilters, ungroupedFilters } = dragDropData;

    const activeItem = activeId
      ? dragDropData.allFilters.find((f) => f.id === activeId) ||
        groups.find((g) => g.id === activeId)
      : null;
    const isFilterBeingDragged = activeItem?.type === 'filter';

    const unifiedItems: (
      | GroupItem
      | FilterItem
      | { type: 'empty-group-dropzone'; groupId: string; id: string }
      | { type: 'ungrouped-dropzone'; id: string }
    )[] = [];

    groups.forEach((group) => {
      unifiedItems.push(group);
      const groupFilters = groupedFilters[group.id] || [];

      if (groupFilters.length > 0) {
        unifiedItems.push(...groupFilters);
      } else {
        unifiedItems.push({
          type: 'empty-group-dropzone',
          groupId: group.id,
          id: `${group.id}-dropzone`,
        });
      }
    });

    unifiedItems.push(...ungroupedFilters);
    if (ungroupedFilters.length === 0 && groups.length > 0) {
      unifiedItems.push({
        type: 'ungrouped-dropzone',
        id: 'ungrouped-dropzone',
      });
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <Box
          sx={{
            width: '100%',
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '8px',
            position: 'relative',
            backgroundColor: activeId ? '#fafbfc' : 'transparent',
            cursor: activeId ? 'grabbing' : 'default',
          }}
        >
          <SortableContext
            items={unifiedItems
              .filter(
                (item) =>
                  item.type !== 'empty-group-dropzone' &&
                  item.type !== 'ungrouped-dropzone'
              )
              .map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {unifiedItems.map((item) => {
              if (item.type === 'group') {
                const group = item as GroupItem;
                const isGroupOver = overId === group.id;
                const isCollapsed = collapsedGroups.has(group.id);
                const isEditing = editingGroupId === group.id;

                return (
                  <Box key={group.id} sx={{ mb: 1 }}>
                    <SortableGroupItem
                      group={group}
                      isOver={isGroupOver}
                      activeId={activeId}
                      isCollapsed={isCollapsed}
                      onToggleCollapse={toggleGroupCollapse}
                      isFilterBeingDragged={isFilterBeingDragged}
                      onEditGroup={handleEditGroup}
                      onDeleteGroup={handleDeleteGroup}
                      isEditing={isEditing}
                      editValue={editGroupName}
                      onEditValueChange={setEditGroupName}
                      onSaveEdit={handleSaveGroupEdit}
                      onCancelEdit={handleCancelGroupEdit}
                    />
                  </Box>
                );
              } else if (item.type === 'empty-group-dropzone') {
                const dropZoneItem = item as {
                  type: 'empty-group-dropzone';
                  groupId: string;
                  id: string;
                };
                const isDropZoneOver = overId === dropZoneItem.id;

                return (
                  <Box key={dropZoneItem.id} sx={{ pl: 3 }}>
                    <EmptyGroupDropZone
                      groupId={dropZoneItem.groupId}
                      isOver={isDropZoneOver}
                      activeId={activeId}
                    />
                  </Box>
                );
              } else if (item.type === 'ungrouped-dropzone') {
                const dropZoneItem = item as {
                  type: 'ungrouped-dropzone';
                  id: string;
                };
                const isDropZoneOver = overId === dropZoneItem.id;

                return (
                  <UngroupedDropZone
                    key={dropZoneItem.id}
                    isOver={isDropZoneOver}
                    isFilterBeingDragged={isFilterBeingDragged}
                  />
                );
              } else {
                const filter = item as FilterItem;
                const isIndented =
                  filter.groupId !== null && filter.groupId !== undefined;

                const isInCollapsedGroup =
                  filter.groupId && collapsedGroups.has(filter.groupId);

                if (isInCollapsedGroup) {
                  return null;
                }

                return (
                  <Box
                    key={filter.id}
                    sx={{
                      mb: 0.5,
                      pl: isIndented ? 3 : 0,
                      '& > *': {
                        width: '100%',
                        maxWidth: '100%',
                      },
                    }}
                  >
                    <SortableFilterItem
                      filter={filter}
                      onApply={handleApplyFilter}
                      onEdit={handleEditFilterAction}
                      onDelete={handleDeleteFilterAction}
                    />
                  </Box>
                );
              }
            })}
          </SortableContext>
        </Box>
      </DndContext>
    );
  };

  const handleClearFilters = () => {
    setShowChips(false);
    reset({ ...defaultFilters(filters) }, { keepDirty: false });
    filterContext?.setFilters({});
    filterContext?.setDynamicFiltersDisplayed(null);
  };

  // Sync form with context filters.
  // - On mount: initialize form from context (if context has values)
  // - On clear: reset form when context is cleared
  const hasInitializedFromContextRef = React.useRef(false);
  const prevContextFiltersRef = React.useRef(filterContext?.filters);

  React.useEffect(() => {
    const rawContextFilters = filterContext?.filters;
    const prevContextFilters = prevContextFiltersRef.current;
    prevContextFiltersRef.current = rawContextFilters;

    if (!rawContextFilters || typeof rawContextFilters !== 'object') {
      return;
    }

    // Flatten grouped context (e.g., { taskFilters: {...} }) into flat form values
    const contextFilters = hasContextKeys
      ? flattenContextFilters(rawContextFilters, filters)
      : rawContextFilters;

    const defaults = defaultFilters(filters);

    // Helper to check if filters object has non-default values
    const hasNonDefaultValues = (filtersObj: Record<string, unknown>) => {
      return Object.keys(filtersObj).some((key) => {
        const value = filtersObj[key];
        const defaultValue = defaults[key];
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return JSON.stringify(value) !== JSON.stringify(defaultValue);
      });
    };

    const currentHasValues = hasNonDefaultValues(contextFilters);
    const prevFlat =
      prevContextFilters && typeof prevContextFilters === 'object'
        ? hasContextKeys
          ? flattenContextFilters(
              prevContextFilters as Record<string, unknown>,
              filters
            )
          : (prevContextFilters as Record<string, unknown>)
        : {};
    const prevHadValues =
      Object.keys(prevFlat).length > 0 ? hasNonDefaultValues(prevFlat) : false;

    // If context was cleared (had values before, empty now), reset form
    if (prevHadValues && !currentHasValues) {
      reset({ ...defaultFilters(filters) }, { keepDirty: false });
      setShowChips(false);
      hasInitializedFromContextRef.current = false;
      return;
    }

    // Sync form from context: on mount (first time) and on context changes.
    // Compares flattened context values against previous to detect changes.
    if (currentHasValues) {
      const contextChanged =
        hasInitializedFromContextRef.current &&
        JSON.stringify(contextFilters) !== JSON.stringify(prevFlat);

      if (!hasInitializedFromContextRef.current || contextChanged) {
        hasInitializedFromContextRef.current = true;

        // Reset all known filter keys to defaults first, then apply context.
        // This ensures removed filters get cleared in the form.
        if (contextChanged) {
          Object.keys(defaults).forEach((key) => {
            setValue(key, defaults[key], { shouldDirty: false });
          });
        }

        Object.keys(contextFilters).forEach((key) => {
          const value = contextFilters[key];
          if (value !== null && value !== undefined) {
            setValue(key, value, { shouldDirty: false });
          }
        });

        setShowChips(true);
      }
    }
  }, [filterContext?.filters, filters, hasContextKeys, setValue, reset]);

  const filterChips = React.useMemo(() => {
    setLossStateFilterInput &&
      setLossStateFilterInput(currentFormValues.lossStates);
    setRsrcStateFilterInput &&
      setRsrcStateFilterInput(currentFormValues.resourceStates);
    return (
      <SelectedChips
        filters={currentFormValues}
        filterLookUp={filterLookUp}
        defaultFilters={defaultFilters(filters)}
        setValue={setValue}
        filterGroups={filters}
      />
    );
  }, [currentFormValues]);

  return (
    <div style={{ zIndex: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            maxWidth: '1150px',
            flexWrap: 'wrap',
            margin: '0 10px',
            maxHeight: '36px',
            rowGap: '1px',
          }}
        >
          {showChips && filterChips}
        </div>
        <Button
          variant="outlined"
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          data-testid="open-filters-button"
          onClick={handleToggle}
          sx={{
            width: '6.625rem',
            height: '2.2813rem',
          }}
        >
          <FilterAltIcon sx={{ fontSize: '18px' }} />
          Filter
        </Button>
      </div>
      <ClickAwayListener
        onClickAway={(event) => {
          if (addFavoriteModalOpen || deleteConfirmModalOpen) {
            return;
          }
          handleClose(event);
        }}
      >
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="right-start"
          transition
          disablePortal
          sx={{
            marginRight: '0.625rem',
            '& .MuiPaper-root': {
              marginRight: '0.625rem',
            },
          }}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: 'right top',
              }}
            >
              <Paper
                sx={{
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: activeId ? '#fafafa' : 'white',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <Box width="30.75rem" padding={'8px 0'}>
                  <Form<Filter>
                    sx={{ width: '100%' }}
                    render={({ control, errors }: RenderFuncProps) => {
                      return (
                        <Stack
                          spacing={2}
                          sx={{
                            width: '100%',
                            overflowY: 'visible',
                            overflowX: 'hidden',
                          }}
                        >
                          {filters && (
                            <FilterList
                              showFilters={
                                showFilterState?.showFilterState ||
                                showFiltersInternal
                              }
                              setShowFilters={
                                showFilterState?.setShowFilterState ||
                                setShowFiltersInternal
                              }
                              control={control}
                              filters={filters}
                              errors={errors}
                              currentFormValues={currentFormValues}
                            />
                          )}
                        </Stack>
                      );
                    }}
                    errors={formErrors}
                    control={formControl}
                    handleSubmit={handleSubmit}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    padding="0.625rem"
                    paddingTop={'10px'}
                  >
                    <Button
                      variant="text"
                      sx={{ fontSize: '0.875rem' }}
                      onClick={() => handleClearFilters()}
                    >
                      Clear All
                    </Button>
                    <Stack direction="row" spacing={1}>
                      {isDirty && !addFavorite ? (
                        <Button
                          variant="contained"
                          color="warning"
                          data-testid="add-to-favorites-bottom"
                          style={{ fontSize: '0.8125rem' }}
                          onClick={() => {
                            setAddFavorite(true);
                            setAddFavoriteModalOpen(true);
                          }}
                        >
                          ADD TO FAVORITES
                        </Button>
                      ) : null}
                      <Button
                        disabled={!isDirty}
                        data-testid="apply-task-filters"
                        variant="contained"
                        color="info"
                        onClick={() => {
                          setShowChips(isValid);
                          handleSubmit(onSubmit)();
                        }}
                      >
                        Apply Filters
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
                {allowSaveFilters && (
                  <Box
                    sx={{
                      backgroundColor: '#F0F1F9',
                      flex: showFavorites ? '0 0 auto' : 'none',
                    }}
                  >
                    <Box>
                      <FavoritesToggle
                        onClick={() => setShowFavorites(!showFavorites)}
                        data-testid="filters-favourites-toggle"
                      >
                        <p style={{ margin: 0, padding: '16px 0' }}>
                          Favorites
                        </p>
                        {showFavorites ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </FavoritesToggle>
                      {showFavorites ? (
                        <>
                          <Box sx={{ padding: '0 1rem 1rem 1rem' }}>
                            <MuiTextField
                              placeholder="Search favorites..."
                              size="small"
                              fullWidth
                              onChange={(e) => debouncedSearch(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon
                                      sx={{ fontSize: '18px', color: '#666' }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#fff',
                                  fontSize: '14px',
                                  borderRadius: '50px',
                                },
                              }}
                            />
                            {/* Enhanced Group Creation UI */}
                            {!showCreateGroup ? (
                              <Button
                                variant="text"
                                sx={{ fontSize: '0.75rem', mt: 1 }}
                                onClick={() => setShowCreateGroup(true)}
                              >
                                <AddIcon /> New Group
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  mt: 1,
                                  display: 'flex',
                                  gap: 1,
                                  alignItems: 'center',
                                }}
                              >
                                <MuiTextField
                                  placeholder="Enter group name..."
                                  size="small"
                                  value={newGroupName}
                                  onChange={(e) =>
                                    setNewGroupName(e.target.value)
                                  }
                                  onKeyDown={async (e) => {
                                    if (e.key === 'Enter') {
                                      if (newGroupName.trim()) {
                                        const result = await createNewGroup(
                                          newGroupName.trim()
                                        );
                                        if (result) {
                                          setNewGroupName('');
                                          setShowCreateGroup(false);
                                        }
                                      }
                                    } else if (e.key === 'Escape') {
                                      setNewGroupName('');
                                      setShowCreateGroup(false);
                                    }
                                  }}
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '12px',
                                    },
                                  }}
                                  autoFocus
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={async () => {
                                    if (newGroupName.trim()) {
                                      const result = await createNewGroup(
                                        newGroupName.trim()
                                      );

                                      if (result) {
                                        setNewGroupName('');
                                        setShowCreateGroup(false);
                                      }
                                    }
                                  }}
                                  sx={{
                                    fontSize: '10px',
                                    minWidth: '50px',
                                    height: '32px',
                                  }}
                                >
                                  Add
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setNewGroupName('');
                                    setShowCreateGroup(false);
                                  }}
                                  sx={{
                                    fontSize: '10px',
                                    minWidth: '50px',
                                    height: '32px',
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            )}
                          </Box>
                          <Box
                            paddingBottom={2}
                            sx={{
                              width: '100%',
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              scrollbarWidth: 'none',
                              msOverflowStyle: 'none',
                              '::-webkit-scrollbar': {
                                display: 'none',
                              },
                            }}
                          >
                            {favorites()}
                          </Box>
                        </>
                      ) : null}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grow>
          )}
        </Popper>
      </ClickAwayListener>
      <AddToFavoritesModal
        watch={watch}
        isDirty={isDirty}
        open={addFavoriteModalOpen}
        onClose={() => {
          if (mode === 'normal') {
            setAddFavoriteModalOpen(false);
            setAddFavorite(false);
            setValue('filterName', '');
            setSelectedGroupForNewFilter(null);
          }
        }}
        mode={mode}
        formControl={formControl}
        availableGroups={dragDropData.groups}
        selectedGroupId={selectedGroupForNewFilter}
        onGroupChange={setSelectedGroupForNewFilter}
        onCreateGroup={createNewGroup}
        onSave={async () => {
          setShowChips(true);
          if (mode === 'edit') {
            await handleSubmit(handleEditFilter)();
          } else {
            await handleSubmit(handleAddToFavorites)();
          }
        }}
        onCancel={() => {
          setAddFavoriteModalOpen(false);
          setAddFavorite(false);
          setSelectedGroupForNewFilter(null);
          if (mode === 'edit') {
            reset();
            setMode('normal');
          }
        }}
      />
      <DeleteConfirmationModal
        open={deleteConfirmModalOpen}
        onClose={handleCancelDeleteGroup}
        onConfirm={handleConfirmDeleteGroup}
        groupName={groupToDelete?.name || ''}
        filterCount={filtersInGroupToDelete.length}
      />
    </div>
  );
}
